import { Box, Link, Stack, Typography, useMediaQuery } from "@mui/material";
import {
  annotateHierarchyHidden,
  ContainerHeader,
  findNodeById,
  findNodeWithMostChildren,
  flattenHierarchyGroupedChildren,
  pitNavData,
} from "@src/helpers/taxonomy";
import {
  animate,
  AnimatePresence,
  motion,
  useAnimationControls,
  useMotionValue,
} from "motion/react";
import * as d3 from "d3";

import { ArcherContainer } from "react-archer";

import React, { FC, useEffect, useRef, useState } from "react";
import NavItem from "./nav-item";
import useHash from "@src/hooks/hash";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors-twentyfive";

import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";

import { useDragControls } from "motion/react";
import { useTheme } from "@mui/material/styles";
import { useWebsiteStore } from "@src/hooks/useWebsiteStore";

const Nav: FC = ({}) => {
  const root = d3.hierarchy(pitNavData);

  const hash = useHash();
  const theme = useTheme();

  const [fullscreen, setFullscreen] = useState(false);
  const [hierarchyLevels, setHierarchyLevels] = useState<any>([]);
  const [hookedLeftOffset, setHookedLeftOffset] = useState(0);
  const [currentDepth, setCurrentDepth] = useState(1);

  const [currentNode, setCurrentNode] = useState<any>(null);

  const arrowsOpacity = useMotionValue(0);

  const archerContainerRef = useRef<any>();
  const parentContainer = useRef<any>();

  const maxNodeCount = findNodeWithMostChildren(root).children.length;

  const dragControls = useDragControls();
  const animationControls = useAnimationControls();

  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const isMedium = useMediaQuery(theme.breakpoints.up("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const mobileExploring = useWebsiteStore((state) => state.mobileExploring);
  const setMobileExploring = useWebsiteStore(
    (state) => state.actions.setMobileExploring,
  );

  useEffect(() => {
    if (!isLarge && hash !== "root" && hash !== "") {
      setMobileExploring(true);
    }
  }, [isLarge, hash]);

  useEffect(() => {
    let timeouts: ReturnType<typeof setTimeout>[] = [];

    const found = findNodeById(root, hash === "" ? "root" : hash);

    setCurrentNode(found ?? null);

    const update = async () => {
      animate(arrowsOpacity, 0, {
        duration: 0.3, // isLarge ? 0.3 : 0,
        onComplete: () => {
          timeouts.push(
            setTimeout(() => {
              if (found) {
                if (isLarge || isMedium) {
                  let width = isLarge ? 500 : 370;
                  let depth = found.data?.depth;

                  if (depth === 0) {
                    setHookedLeftOffset((found.data?.depth + 2) * width);
                  }

                  if (depth > 0) {
                    setHookedLeftOffset(
                      (found.data?.depth + 1) * width +
                        (found.data?.depth - 1) * 20,
                    );
                  }
                } else {
                  let depth = found.data?.depth;

                  if (depth === 0) {
                    setHookedLeftOffset(
                      (found.data?.depth + (mobileExploring ? 2 : 1)) * 300 +
                        (found.data?.depth + (mobileExploring ? 1 : 0)) * 20,
                    );
                  }

                  if (depth > 0) {
                    setHookedLeftOffset(
                      (found.data?.depth + (mobileExploring ? 2 : 1)) * 300 +
                        (found.data?.depth + 1) * 20,
                    );
                  }
                }

                setCurrentDepth(found.data.depth ?? 0);
              }

              const hidden = annotateHierarchyHidden(
                root,
                hash === "" ? "root" : hash,
              );
              const levels = flattenHierarchyGroupedChildren(hidden);

              setHierarchyLevels([[[root]], ...levels]);
            }, 50),
          );

          timeouts.push(
            setTimeout(() => {
              animate(arrowsOpacity, 1, { duration: 0.3 });
            }, 500),
          );

          for (let i = 0; i <= 10; i++) {
            timeouts.push(
              setTimeout(
                () => {
                  archerContainerRef?.current?.refreshScreen();
                },
                400 + i * 50,
              ),
            );
          }
        },
      });
    };

    update();

    return () => {
      for (const timeoutInstance of timeouts) {
        clearTimeout(timeoutInstance);
      }
    };
  }, [hash, mobileExploring, isLarge, isMedium]);

  useEffect(() => {
    if (!fullscreen) {
      animationControls.set({
        x: 0,
        y: 0,
      });
    }
  }, [fullscreen]);

  const [height, setHeight] = useState(maxNodeCount * (40 + 16));

  useEffect(() => {
    if (isLarge) {
      setHeight(maxNodeCount * (40 + 16));
    } else {
      setHeight((maxNodeCount + 1) * (30 + 16));
    }
  }, [isLarge]);

  return (
    <>
      <Box
        sx={{
          position: "relative",
        }}
      >
        <Box
          sx={{
            zIndex: -100,
            height: `${height}px`,
          }}
        ></Box>

        <Box
          sx={{
            position: "fixed",
            zIndex: -110,
            inset: 0,
            display: fullscreen ? "block" : "none",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            pointerEvents: "none",
          }}
        ></Box>

        <motion.div
          layout
          drag={fullscreen}
          dragControls={dragControls}
          style={
            {
              position: fullscreen ? "fixed" : "absolute",
              height: fullscreen ? "calc(100vh - 53px)" : `${height}px`,
              width: "100%",
              // border: `solid 1px #FFFF00`,
              top: fullscreen ? "53px" : "0px",
              zIndex: fullscreen ? 100 : "initial",
              overflowX: "hidden",
              overflowY: "hidden",
              "--taxonomy-line-opacity": arrowsOpacity,
            } as React.CSSProperties
          }
          ref={parentContainer}
        >
          <ArcherContainer ref={archerContainerRef}>
            <motion.div
              style={{
                position: "relative",
                height: fullscreen ? "calc(100vh - 53px)" : `${height}px`,
                transformOrigin: "50% 50%",
              }}
              animate={{
                transform: fullscreen
                  ? `scale3d(0.5, 0.5, 1)`
                  : `scale3d(1, 1, 1)`,
              }}
              transition={{
                ease: "easeInOut",
                duration: 0.5,
              }}
            >
              {currentNode?.id !== "root" && mobileExploring && (
                <Link
                  role="button"
                  href={
                    currentNode?.parent?.data?.categoryId
                      ? `#${currentNode?.parent?.data?.categoryId}`
                      : "#"
                  }
                  sx={{
                    position: "fixed",
                    display: {
                      xs: "block",
                      md: "none",
                    },
                    left: "16px",
                    bottom: "0px",
                    color: Colors.PrimaryContrast,
                    textDecoration: "none",
                    "&:visited": {
                      color: Colors.PrimaryContrast,
                    },
                    zIndex: 100,

                    border: `solid 1px ${Colors.AiOutline}`,
                    borderRadius: "100px",
                    padding: "5px 10px",
                  }}
                  onClick={(e) => {
                    if (currentNode?.data?.name === "root") {
                      setMobileExploring(false);
                    }
                  }}
                >
                  Back
                </Link>
              )}
              <Box
                sx={{
                  flexDirection: "row",
                  gap: "10px",
                  paddingX: "16px",
                  display: {
                    xs: "flex",
                    md: "none",
                  },
                }}
              >
                <ContainerHeader
                  variant="h1"
                  sx={{
                    background:
                      "linear-gradient(90deg, #FFCDAF 12.35%, #8C48EB 88.07%)",
                    backgroundClip: "text",
                    textFillColor: "transparent",
                    marginX: "auto",
                    fontSize: {
                      xs: "20px",
                      md: "initial",
                    },
                    lineHeight: {
                      xs: "22px",
                      md: "initial",
                    },
                  }}
                >
                  {currentNode?.data.categoryId !== "root"
                    ? currentNode?.data?.name
                    : ""}
                </ContainerHeader>
              </Box>
              <AnimatePresence>
                <Box
                  sx={{
                    position: "absolute",
                    // top: fullscreen ? "0px" : "50%",
                    left: fullscreen ? "0px" : "50%",
                    height: "100%",
                    top: isLarge ? "auto" : "0px",
                    // border: `solid 1px #0000FF`,
                    marginLeft: isLarge ? "250px" : "150px",
                    transform: `translate3d(-${hookedLeftOffset}px, 0px, 0px)`,
                    transition: `transform 290ms cubic-bezier(0.040, 0.220, 0.000, 1.000)`,
                  }}
                >
                  <Stack
                    sx={{
                      flexDirection: "row",
                      gap: "20px",
                      alignItems: "center",
                    }}
                  >
                    {hierarchyLevels.map((level: any, levelIndex: number) => {
                      return (
                        <Stack
                          key={`level-${levelIndex}`}
                          sx={{
                            flexDirection: "column",
                            // border: `solid 1px #00FF00`,
                            width: {
                              xs: "300px",
                              md: "370px",
                              lg: "500px",
                            },
                            gap: fullscreen ? "28px" : "0px",
                          }}
                        >
                          {currentNode?.height === 0 && !isLarge && (
                            <Typography
                              data-testid="label-no-sub-categories"
                              sx={{
                                position: "relative",
                                display: {
                                  md: "none",
                                  xs:
                                    currentNode?.data?.depth +
                                      (mobileExploring ? 1 : 0) ===
                                    levelIndex
                                      ? "block"
                                      : "none",
                                },
                                opacity: 0.7,
                                top: "100px",
                                color: Colors.PrimaryContrast,
                                marginX: "auto",
                              }}
                            >
                              No sub categories
                            </Typography>
                          )}
                          {level.map((childNode: any, pathIndex: number) => {
                            return (
                              <Stack
                                key={`level-${levelIndex}-child-${pathIndex}`}
                                sx={{
                                  position: fullscreen
                                    ? "relative"
                                    : "absolute",
                                  top: `50%`,
                                  transform: "translate3d(0px, -50%, 0px)",
                                  display: {
                                    md: "flex",
                                    xs:
                                      currentNode?.data?.depth +
                                        (mobileExploring ? 1 : 0) ===
                                      levelIndex
                                        ? "flex"
                                        : "none",
                                  },
                                  // border: `solid 1px #F9ADAB`
                                }}
                              >
                                {childNode.map(
                                  (child: any, childIndex: number) => {
                                    return (
                                      <NavItem
                                        navItem={child}
                                        key={`level-${levelIndex}-child-${pathIndex}-${childIndex}`}
                                        currentDepth={currentDepth}
                                        fullscreen={fullscreen}
                                      />
                                    );
                                  },
                                )}
                              </Stack>
                            );
                          })}
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              </AnimatePresence>
            </motion.div>

            {/*<Link
              sx={{
                display: "flex",
                gap: "6px",
                alignItems: "middle",
                position: "absolute",
                bottom: "10px",
                right: "10px",
                color: Colors.PrimaryContrast,
                "&:hover": {
                  cursor: "pointer",
                },
              }}
              onClick={() => {
                setFullscreen(!fullscreen);
              }}
            >
              {fullscreen ? "Exit " : ""}Fullscreen{" "}
              {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </Link>*/}
          </ArcherContainer>
        </motion.div>
      </Box>
    </>
  );
};

export default Nav;
